import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Mail, Phone, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { mailHref, telHref, useSiteConfig } from "@/lib/site-config";
import logoUrl from "@/assets/logo-final.png";

const MENU_LINKS: { to: string; label: string; end?: boolean }[] = [
  { to: "/", label: "Home", end: true },
  { to: "/properties", label: "Properties" },
  { to: "/areas", label: "Communities" },
  { to: "/developers", label: "Developers" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/list-property", label: "List Your Property" },
  { to: "/contact", label: "Contact Us" },
];

/** Menu rows that act as calls to action rather than sections — they
 *  repeat destinations already in MENU_LINKS, so they render as plain
 *  links without an active state. */
const MENU_ACTIONS: { to: string; label: string }[] = [
  { to: "/contact", label: "Book a Consultation" },
  { to: "/properties", label: "Find a Property" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const siteConfig = useSiteConfig();

  const overlayRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  /* Move focus into the dialog on open; hand it back to the Menu
     button on close so keyboard users never lose their place. */
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      // Delay until the panel's visibility transition has kicked in —
      // focus() is a no-op while the dialog is still visibility:hidden.
      const id = window.setTimeout(() => closeButtonRef.current?.focus(), 60);
      return () => window.clearTimeout(id);
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      menuButtonRef.current?.focus();
    }
  }, [open]);

  /* Escape closes; Tab is trapped inside the open dialog. */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        return;
      }
      if (e.key !== "Tab") return;
      const root = overlayRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && root.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      {/* Top bar — Menu · centred logo · actions */}
      <header
        data-on-dark
        className={cn(
          "sticky top-0 z-50 bg-[rgba(13,20,34,0.94)] backdrop-blur-md transition-[border-color,box-shadow] duration-300",
          scrolled
            ? "border-b border-[rgba(240,234,216,0.30)] shadow-[0_12px_30px_-20px_rgba(0,0,0,0.85)]"
            : "border-b border-[rgba(240,234,216,0.16)]",
        )}
      >
        <div className="container-wide grid h-16 grid-cols-[1fr_auto_1fr] items-center md:h-[76px] short:h-14">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setOpen(true)}
            aria-expanded={open}
            aria-controls="site-menu"
            aria-haspopup="dialog"
            className="focus-ring -ml-2 flex min-h-[44px] items-center gap-2.5 justify-self-start px-2 text-[13.5px] font-medium text-[var(--text-on-dark)] transition-colors hover:text-[var(--cream)]"
          >
            <svg width="20" height="12" viewBox="0 0 20 12" fill="none" aria-hidden>
              <path d="M0 1h20M0 11h20" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <span>Menu</span>
          </button>

          <Link
            to="/"
            aria-label="Hive Homes · home"
            className="focus-ring flex items-center justify-self-center"
          >
            <img
              src={logoUrl}
              alt="Hive Homes"
              className="h-10 w-auto sm:h-11 md:h-12 short:h-9"
              width={600}
              height={355}
            />
          </Link>

          <div className="flex items-center gap-1 justify-self-end sm:gap-4 md:gap-6">
            <Link
              to="/contact"
              className="focus-ring hidden items-center rounded-full border border-[rgba(240,234,216,0.35)] px-4.5 py-2 text-[12.5px] font-medium text-[var(--text-on-dark)] transition-colors hover:border-[var(--cream)] hover:text-[var(--cream)] lg:inline-flex"
            >
              Book a Consultation
            </Link>
            <Link
              to="/properties"
              aria-label="Find a Property"
              className="focus-ring flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 px-2 text-[13.5px] font-medium text-[var(--text-on-dark)] transition-colors hover:text-[var(--cream)] sm:min-w-0 sm:-mr-2 sm:justify-start"
            >
              <Search className="size-[17px]" strokeWidth={1.6} aria-hidden />
              <span className="hidden sm:inline">Find a Property</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Full-screen menu — navy panel, logo top-left, circled close
          top-right, plain left-aligned link list. */}
      <div
        ref={overlayRef}
        id="site-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        aria-hidden={!open}
        data-on-dark
        className={cn(
          "fixed inset-0 z-[60] flex flex-col bg-[var(--navy-deep)] transition-[opacity,visibility] duration-400",
          open ? "visible opacity-100" : "invisible opacity-0",
        )}
      >
        <div className="container-wide flex h-16 flex-none items-center justify-between md:h-[76px] short:h-14">
          <Link
            to="/"
            aria-label="Hive Homes · home"
            onClick={close}
            className="focus-ring flex items-center"
          >
            <img
              src={logoUrl}
              alt="Hive Homes"
              className="h-10 w-auto sm:h-11 md:h-12 short:h-9"
              width={600}
              height={355}
            />
          </Link>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            aria-label="Close menu"
            className="focus-ring -mr-1.5 flex size-11 items-center justify-center text-[var(--text-on-dark)] transition-colors hover:text-[var(--cream)]"
          >
            <span className="flex size-9 items-center justify-center rounded-full border border-[rgba(240,234,216,0.38)] transition-colors hover:border-[var(--cream)]">
              <X className="size-4" strokeWidth={1.4} aria-hidden />
            </span>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain">
          <div className="container-wide flex min-h-full flex-col pb-12 pt-5 md:pt-8">
            <ul className="flex flex-col">
              {MENU_LINKS.map((item, i) => (
                <li key={item.label}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={close}
                    style={{ transitionDelay: open ? `${60 + i * 35}ms` : "0ms" }}
                    className={({ isActive }) =>
                      cn(
                        "block py-3 text-[18px] leading-snug transition-all duration-500 md:py-3.5 md:text-[20px] short:py-2 short:text-[16px]",
                        open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
                        isActive
                          ? "text-[var(--gold-bright)]"
                          : "text-[var(--text-on-dark)] hover:text-[var(--cream)]",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              {MENU_ACTIONS.map((item, i) => (
                <li key={item.label}>
                  <Link
                    to={item.to}
                    onClick={close}
                    style={{
                      transitionDelay: open
                        ? `${60 + (MENU_LINKS.length + i) * 35}ms`
                        : "0ms",
                    }}
                    className={cn(
                      "block py-3 text-[18px] leading-snug text-[var(--text-on-dark)] transition-all duration-500 hover:text-[var(--cream)] md:py-3.5 md:text-[20px] short:py-2 short:text-[16px]",
                      open ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Contact details — desktop menu only; on phones the list
                stays clean per the client's request. */}
            <div className="mt-auto hidden flex-col gap-2.5 pt-10 text-[13.5px] text-[var(--text-on-dark-soft)] md:flex">
              {siteConfig.phone.set && (
                <a
                  href={telHref(siteConfig.phone)}
                  className="focus-ring tap-target flex w-fit items-center gap-2 transition-colors hover:text-[var(--cream)]"
                >
                  <Phone className="size-3.5" aria-hidden />
                  {siteConfig.phone.display}
                </a>
              )}
              {siteConfig.email.set && (
                <a
                  href={mailHref(siteConfig.email)}
                  className="focus-ring tap-target flex w-fit items-center gap-2 transition-colors hover:text-[var(--cream)]"
                >
                  <Mail className="size-3.5" aria-hidden />
                  {siteConfig.email.display}
                </a>
              )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
}
